import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Paper, Skeleton, Typography } from "@mui/material";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { apiService, SubscriptionPricingResponse } from "../services/api";
import { formatPriceShort } from "../utils/formatCurrency";
import SalesClosedNotice from "./SalesClosedNotice";

// Bloco de ofertas público da landing: puxa preço e contador de licenças ao vivo
// dos endpoints públicos (/doctor/subscription-pricing, /doctor/lifetime-licenses)
// e leva o profissional ao fluxo de compra (/signup?plan=...).

const sharedPerks = [
  "Diagnósticos assistidos por agentes clínicos",
  "Disponível 24 horas por dia",
  "Histórico completo de conversas",
  "Resultados exportáveis",
];

export default function PricingSection() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState<SubscriptionPricingResponse | null>(null);
  const [licensesAvailable, setLicensesAvailable] = useState<number | null>(null);
  const [trialDays, setTrialDays] = useState(7);

  useEffect(() => {
    apiService
      .getSubscriptionPricing()
      .then((data) => {
        setPricing(data);
        if (data.trial) setTrialDays(data.trial.days);
      })
      .catch(() => {});

    apiService
      .getLifetimeLicenses()
      .then((data) => setLicensesAvailable(data.available))
      .catch(() => {});
  }, []);

  if (pricing?.salesOpen === false) {
    return (
      <section id="planos" className="relative z-10 px-6 md:px-12 pb-20 md:pb-28">
        <SalesClosedNotice />
      </section>
    );
  }

  const priceLoading = !pricing;
  const monthlyPrice = pricing ? formatPriceShort(pricing.monthly.amount) : "";
  const annualPrice = pricing?.annual ? formatPriceShort(pricing.annual.upfront.amount) : "";
  const lifetimePrice = pricing ? formatPriceShort(pricing.lifetime.baseAmount) : "";

  // Economia do anual vs. 12x do mensal (ex.: ~60%), calculada do pricing.
  const annualSavings =
    pricing?.annual && pricing.monthly.amount > 0
      ? Math.round((1 - pricing.annual.upfront.amount / (pricing.monthly.amount * 12)) * 100)
      : null;

  const trialCta = `Começar ${trialDays} dias grátis`;

  return (
    <section id="planos" className="relative z-10 px-6 md:px-12 pb-20 md:pb-28">
      <div className="text-center mb-14">
        <Typography
          variant="h4"
          sx={{
            color: "#ffffff",
            fontWeight: 700,
            fontFamily: "Oxanium, sans-serif",
            letterSpacing: "-0.02em",
            marginBottom: "12px",
            fontSize: { xs: "1.5rem", md: "2rem" },
          }}
        >
          Escolha seu plano e comece hoje
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.6)",
            fontSize: "1.05rem",
            maxWidth: "560px",
            margin: "0 auto",
          }}
        >
          Experimente {trialDays} dias grátis nos planos mensal e anual. Cancele quando quiser.
        </Typography>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto items-stretch">
        {/* Mensal */}
        <PlanCard
          label="Mensal"
          price={monthlyPrice}
          loading={priceLoading}
          suffix="/mês"
          subtitle="Flexível, cancele quando quiser"
          perks={sharedPerks}
          ctaText={trialCta}
          ctaHint="Sem cobrança hoje"
          onClick={() => navigate("/signup?plan=monthly&trial=1")}
        />

        {/* Anual — destaque/recomendado */}
        <PlanCard
          label="Anual"
          price={annualPrice}
          loading={priceLoading}
          suffix="/ano"
          subtitle={
            annualSavings != null
              ? `Economize ${annualSavings}% frente ao mensal`
              : "Melhor custo-benefício"
          }
          perks={sharedPerks}
          ctaText={trialCta}
          ctaHint="Sem cobrança hoje"
          highlighted
          ribbon="Recomendado"
          onClick={() => navigate("/signup?plan=annual&trial=1")}
        />

        {/* Vitalício — escassez */}
        <PlanCard
          label="Vitalício"
          price={lifetimePrice}
          loading={priceLoading}
          suffix=" único"
          subtitle="Pague uma vez, acesso para sempre"
          perks={[...sharedPerks, "Acesso vitalício sem renovação"]}
          scarcity={licensesAvailable != null ? `${licensesAvailable} de 100 licenças disponíveis` : undefined}
          ctaText="Garantir acesso vitalício"
          ctaHint="Cartão (até 12x) ou PIX"
          onClick={() => navigate("/signup?plan=lifetime")}
        />
      </div>
    </section>
  );
}

interface PlanCardProps {
  label: string;
  price: string;
  loading: boolean;
  suffix: string;
  subtitle: string;
  perks: string[];
  scarcity?: string;
  ctaText: string;
  ctaHint?: string;
  highlighted?: boolean;
  ribbon?: string;
  onClick: () => void;
}

function PlanCard({
  label,
  price,
  loading,
  suffix,
  subtitle,
  perks,
  scarcity,
  ctaText,
  ctaHint,
  highlighted = false,
  ribbon,
  onClick,
}: PlanCardProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        background: highlighted
          ? "linear-gradient(135deg, rgba(255, 137, 4, 0.18) 0%, rgba(26, 49, 60, 0.7) 100%)"
          : "linear-gradient(135deg, rgba(26, 49, 60, 0.6) 0%, rgba(4, 9, 10, 0.8) 100%)",
        backdropFilter: "blur(16px)",
        border: highlighted
          ? "2px solid rgba(255, 137, 4, 0.55)"
          : "1px solid rgba(255,255,255,0.08)",
        borderRadius: "24px",
        padding: "32px",
        boxShadow: highlighted ? "0 16px 48px rgba(255, 137, 4, 0.25)" : "none",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          border: "1px solid rgba(255, 137, 4, 0.4)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
        },
      }}
    >
      {ribbon && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span
            className="px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-white"
            style={{ background: "linear-gradient(135deg, #FF8904 0%, #E7000B 100%)" }}
          >
            {ribbon}
          </span>
        </div>
      )}

      <Typography
        sx={{
          color: "rgba(255,255,255,0.85)",
          fontWeight: 700,
          fontSize: "1.05rem",
          fontFamily: "Oxanium, sans-serif",
          marginBottom: "6px",
        }}
      >
        {label}
      </Typography>

      <div className="flex items-baseline gap-1 mb-2">
        {loading ? (
          <Skeleton
            variant="text"
            width={100}
            sx={{ fontSize: "2.2rem", bgcolor: "rgba(255,255,255,0.13)" }}
          />
        ) : (
          <Typography
            sx={{
              color: "#FF8904",
              fontWeight: 800,
              fontSize: "2.2rem",
              fontFamily: "Oxanium, sans-serif",
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            {price}
          </Typography>
        )}
        <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.95rem" }}>
          {suffix}
        </Typography>
      </div>

      <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.9rem", marginBottom: "20px" }}>
        {subtitle}
      </Typography>

      {scarcity && (
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 700,
            marginBottom: "16px",
            background:
              "linear-gradient(90deg, #FF8904 0%, #FF8904 40%, #FFD700 50%, #FF8904 60%, #FF8904 100%)",
            backgroundSize: "200% 100%",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            animation: "shimmer 3s ease-in-out infinite",
            "@keyframes shimmer": {
              "0%": { backgroundPosition: "100% 0" },
              "100%": { backgroundPosition: "-100% 0" },
            },
          }}
        >
          {scarcity}
        </Typography>
      )}

      <div className="space-y-2.5 mb-8 flex-1">
        {perks.map((perk) => (
          <div key={perk} className="flex items-start gap-2.5">
            <CheckOutlinedIcon sx={{ fontSize: 18, color: "#FF8904", marginTop: "2px" }} />
            <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.9rem", lineHeight: 1.5 }}>
              {perk}
            </Typography>
          </div>
        ))}
      </div>

      <Button
        onClick={onClick}
        variant={highlighted ? "contained" : "outlined"}
        fullWidth
        sx={
          highlighted
            ? {
                background: "linear-gradient(135deg, #FF8904 0%, #E7000B 100%)",
                borderRadius: "16px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 700,
                padding: "14px 24px",
                color: "#ffffff",
                boxShadow: "0 8px 24px rgba(255, 137, 4, 0.4)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  background: "linear-gradient(135deg, #E7000B 0%, #FF8904 100%)",
                },
              }
            : {
                borderRadius: "16px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 700,
                padding: "14px 24px",
                color: "#FF8904",
                borderColor: "rgba(255, 137, 4, 0.5)",
                "&:hover": {
                  borderColor: "#FF8904",
                  backgroundColor: "rgba(255, 137, 4, 0.08)",
                },
              }
        }
      >
        {ctaText}
      </Button>

      {ctaHint && (
        <Typography
          sx={{
            color: "rgba(255,255,255,0.4)",
            fontSize: "11px",
            textAlign: "center",
            marginTop: "10px",
          }}
        >
          {ctaHint}
        </Typography>
      )}
    </Paper>
  );
}
